import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Copy } from 'lucide-react';
import {
  Button,
  DataTable,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  AlertMessage,
  PageHeader,
  SearchFilterBar,
  FilterTabs,
} from '../common/ui';
import Pagination from '../common/Pagination';
import { useAccountingActionBar } from './AccountingActionBar';
import LinkedFlag from './LinkedFlag';
import { formatDateTime } from '../../utils/dateUtils';
import type { B2BOrder, PaginationInfo, LinkedFilter } from './types';
import { getWooCommerceOrderUrl } from './types';

// One tab per linkage leg, mirroring the PAYMENT and VASCO columns.
const LINKED_FILTERS: { key: LinkedFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'linked', label: 'Payment linked' },
  { key: 'not-linked', label: 'Payment not linked' },
  { key: 'force-closed', label: 'Force closed' },
  { key: 'overpaid', label: 'Overpaid' },
  { key: 'vasco-linked', label: 'Vasco linked' },
  { key: 'vasco-not-linked', label: 'Vasco not linked' },
];

// The PAYMENT column. Overpaid is orange: two or more payments together pay more
// than the order total, so there may be a double charge or a wrong link to check.
const PAYMENT_LINK_BADGES: Record<B2BOrder['is_linked'], { label: string; className: string }> = {
  overpaid: { label: 'Overpaid', className: 'text-orange-700 bg-orange-50' },
  full: { label: 'Linked', className: 'text-green-700 bg-green-50' },
  partial: { label: 'Partial', className: 'text-blue-700 bg-blue-50' },
  none: { label: 'Not linked', className: 'text-red-600 bg-red-50' },
  force_closed: { label: 'Force closed', className: 'text-teal-700 bg-teal-50' },
};

const B2BOrders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<B2BOrder[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 50, total: 0 });
  const [stats, setStats] = useState<{
    page: { total: number; linked: number; notLinked: number; vascoLinked: number };
    last30Days: { total: number; linked: number; notLinked: number; vascoLinked: number };
    lastYear: { total: number; linked: number; notLinked: number; vascoLinked: number };
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [linkedFilter, setLinkedFilter] = useState<LinkedFilter>(
    (searchParams.get('linked') as LinkedFilter) || 'all'
  );
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );

  const fetchOrders = useCallback(async (page: number, search: string, linked: LinkedFilter, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '50' };
      if (search) params.search = search;
      if (linked !== 'all') params.linked = linked;

      const { data } = await axios.get('/api/b2b/orders/accounting', { params, signal });
      if (signal?.aborted) return;
      setFetchError('');
      setOrders(data.orders);
      setPagination(data.pagination);
      if (data.stats) setStats(data.stats);
    } catch (err: any) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return;
      setFetchError(err.response?.data?.error || 'Failed to load B2B orders');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchOrders(currentPage, searchTerm, linkedFilter, controller.signal);
    return () => controller.abort();
  }, [fetchOrders, currentPage, searchTerm, linkedFilter]);

  const refetchCurrentPage = () => fetchOrders(currentPage, searchTerm, linkedFilter);

  const { actionButtons, actionAlerts } = useAccountingActionBar(refetchCurrentPage);

  const updateUrlParams = (linked: LinkedFilter, search: string, page: number) => {
    const params = new URLSearchParams();
    if (linked && linked !== 'all') params.set('linked', linked);
    if (search && search.trim()) params.set('search', search.trim());
    if (page > 1) params.set('page', String(page));
    setSearchParams(params);
  };

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    setSearchTerm(trimmed);
    setCurrentPage(1);
    updateUrlParams(linkedFilter, trimmed, 1);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
    updateUrlParams(linkedFilter, '', 1);
  };

  const handleLinkedFilterChange = (filter: LinkedFilter) => {
    setLinkedFilter(filter);
    setCurrentPage(1);
    updateUrlParams(filter, searchTerm, 1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updateUrlParams(linkedFilter, searchTerm, page);
  };

  useEffect(() => {
    const linkedFromUrl = (searchParams.get('linked') as LinkedFilter) || 'all';
    const searchFromUrl = searchParams.get('search') || '';
    const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
    if (linkedFromUrl !== linkedFilter) setLinkedFilter(linkedFromUrl);
    if (searchFromUrl !== searchTerm) {
      setSearchInput(searchFromUrl);
      setSearchTerm(searchFromUrl);
    }
    if (pageFromUrl !== currentPage) setCurrentPage(pageFromUrl);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const formatCustomer = (order: B2BOrder) => {
    const company = order.billing_company?.trim();
    const firstName = order.billing_first_name?.trim() || '';
    const lastName = order.billing_last_name?.trim() || '';
    const personalName = [firstName, lastName].filter(Boolean).join(' ');

    if (company && personalName) {
      return (
        <div>
          <span className="text-sm font-medium block truncate max-w-[200px]" title={company}>
            {company}
          </span>
          <span className="text-xs text-gray-500 block truncate max-w-[200px]" title={personalName}>
            {personalName}
          </span>
        </div>
      );
    }

    const display = company || personalName || order.customer_name || '—';
    return (
      <span className="text-sm block truncate max-w-[200px]" title={display}>
        {display}
      </span>
    );
  };

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="B2B Orders"
        subtitle="Imported WooCommerce orders grouped by B2B order"
        actions={actionButtons}
      />

      {actionAlerts}
      {fetchError && (
        <AlertMessage type="error" message={fetchError} title="" onDismiss={() => setFetchError('')} dismissible>
          {''}
        </AlertMessage>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting')}
          icon={ArrowLeft}
        >
          Group by Bank transfer
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate('/accounting/vasco-invoices')}
          icon={ArrowRight}
        >
          Group by Vasco invoice
        </Button>
      </div>

      <SearchFilterBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        onSearchSubmit={handleSearch}
        onSearch={handleSearch}
        onSearchClear={handleSearchClear}
        searchPlaceholder="Search by order number, customer name, or email..."
        onFilterChange={() => {}}
      />

      <FilterTabs
        filters={LINKED_FILTERS}
        activeFilter={linkedFilter}
        onFilterChange={handleLinkedFilterChange}
      />

      <DataTable emptyState={null}>
        <TableHeader>
          <tr>
            <TableHeaderCell responsive={undefined}>Order #</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Date & Time</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Customer</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Payment Source</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Amount</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Payment</TableHeaderCell>
            <TableHeaderCell responsive={undefined}>Vasco</TableHeaderCell>
          </tr>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Loading orders…"
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : fetchError ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message="Unable to load B2B orders."
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : orders.length === 0 ? (
            <TableRow onClick={undefined} href={undefined} onKeyDown={undefined}>
              <TableCell responsive={undefined} colSpan={7} className="p-0">
                <EmptyState
                  message={
                    searchTerm
                      ? `No orders match "${searchTerm}".`
                      : 'No B2B orders found. Sync orders from the Accounting page first.'
                  }
                  action={false}
                  actionText=""
                  onActionClick={() => {}}
                />
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order) => (
              <TableRow
                key={order.id}
                clickable={true}
                onClick={() => navigate(`/accounting/b2b-orders/${order.id}`)}
                href={`/accounting/b2b-orders/${order.id}`}
                onKeyDown={undefined}
              >
                <TableCell responsive={undefined} className="whitespace-nowrap group">
                  <div className="flex items-center gap-2">
                    <a
                      href={getWooCommerceOrderUrl(order.woo_order_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      #{order.order_number}
                    </a>
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 focus:opacity-100 focus:outline-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(order.order_number);
                      }}
                      title="Copy order number"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </TableCell>
                <TableCell responsive={undefined}>{formatDateTime(order.order_date)}</TableCell>
                <TableCell responsive={undefined}>
                  {formatCustomer(order)}
                </TableCell>
                <TableCell responsive={undefined}>
                  {order.payment_method ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {order.payment_method}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell responsive={undefined}>
                  <span className="font-medium tabular-nums text-green-700">
                    {order.currency} {parseFloat(order.total).toFixed(2)}
                  </span>
                </TableCell>
                <TableCell responsive={undefined}>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PAYMENT_LINK_BADGES[order.is_linked].className}`}
                    title={
                      order.is_linked === 'overpaid' ? 'Two or more payments together pay more than the order total'
                      : order.is_linked === 'force_closed' ? order.force_close_note || undefined
                      : undefined
                    }
                  >
                    {PAYMENT_LINK_BADGES[order.is_linked].label}
                  </span>
                </TableCell>
                <TableCell responsive={undefined}>
                  <LinkedFlag linked={order.has_vasco} title="Linked to Vasco invoice" />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </DataTable>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={() => {}}
        showItemsPerPageSelector={false}
      />

      {/* Stats footer */}
      {stats && (
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          {([
            ['Current page', stats.page],
            ['Last 30 days', stats.last30Days],
            ['Last year', stats.lastYear],
          ] as [string, { total: number; linked: number; notLinked: number; vascoLinked: number }][]).map(([label, s]) => (
            <div key={label} className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-400 font-medium mb-1.5">{label}</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-gray-600 font-medium">{s.total}</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-green-700 bg-green-50">
                  {s.linked} payment-linked
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-blue-700 bg-blue-50">
                  {s.vascoLinked} vasco-linked
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default B2BOrders;
